import { computeRaiseImpact } from './computeRaiseImpact';

const round2 = (num) => Math.round(num * 100) / 100;

/**
 * Solves for the required gross raise percentage to achieve a target net monthly increase
 * Uses monotonic binary search over raise percentages
 * 
 * @param {Object} input - TargetNetInput
 * @param {number} input.currentAnnualGross - Current annual gross salary
 * @param {number} input.targetNetMonthlyIncrease - Target net monthly increase
 * @param {Object} [input.context] - Engine context (region, student loans, pension, etc.)
 * @returns {Object} TargetNetResult
 */
export const solveGrossForTargetNet = (input) => {
  const { currentAnnualGross, targetNetMonthlyIncrease, context = {} } = input;

  // Validation
  if (currentAnnualGross <= 0) {
    throw new Error('Current salary must be greater than zero');
  }

  if (targetNetMonthlyIncrease <= 0) {
    throw new Error('Target must be greater than zero');
  }

  const targetNetAnnual = targetNetMonthlyIncrease * 12;
  const tolerance = 1; // £1/month tolerance (£12/year)

  // Binary search parameters
  let lowPct = 0;
  let highPct = 100;
  const maxIterations = 50; // Prevent infinite loops
  let iterations = 0;

  // First check if the target is even achievable with a 100% raise
  const maxRaiseResult = computeRaiseImpact({
    currentAnnualGross,
    raisePct: 100,
    context
  });

  if (targetNetAnnual > maxRaiseResult.deltaNetAnnual) {
    throw new Error('Target too high for given constraints');
  }

  // Binary search for the correct raise percentage
  while (iterations < maxIterations) {
    const midPct = (lowPct + highPct) / 2;
    
    const result = computeRaiseImpact({
      currentAnnualGross,
      raisePct: midPct,
      context
    });

    const netAnnualDelta = result.deltaNetAnnual;
    const difference = Math.abs(netAnnualDelta - targetNetAnnual);

    // Check if we're within tolerance
    if (difference <= tolerance) {
      const requiredGrossAnnualIncrease = result.deltaGrossAnnual;
      const requiredRaisePctGross = midPct;

      return {
        requiredGrossAnnualIncrease: round2(requiredGrossAnnualIncrease),
        requiredRaisePctGross: round2(requiredRaisePctGross)
      };
    }

    // Adjust search range
    if (netAnnualDelta < targetNetAnnual) {
      lowPct = midPct;
    } else {
      highPct = midPct;
    }

    iterations++;
  }

  // If we reach here, the search didn't converge
  // This should rarely happen with well-behaved tax functions
  throw new Error('Unable to converge on solution within tolerance');
};